import { makeAutoObservable } from 'mobx'
import { PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { CallModalStore } from 'widgets/CallModal'
import { ContactCreateModalStore } from 'widgets/ContactCreateModal'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ConversationSearchStore } from 'pages/conversations/ui/ConversationHeaderSearch'
import { ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import { ConversationRoutes } from 'pages/conversations/route/type'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import { openResultsSearch } from 'pages/conversations/pages/search'

export class ConversationsPageLayoutStore {
  private readonly _callModalStore: CallModalStore
  private readonly _contactCreateModalStore: ContactCreateModalStore
  private readonly _conversationMenuStore: ConversationMenuStore
  private readonly _conversationSearchStore: ConversationSearchStore
  private readonly _contactsDetailsManageStore: ContactsDetailsManageStore
  private _pageLayoutStore = new PageLayoutStore()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._callModalStore = new CallModalStore(this._entities)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._contactCreateModalStore = new ContactCreateModalStore(
      this._callModalStore,
      this._entities
    )
    this._conversationMenuStore = new ConversationMenuStore(
      this._pageLayoutStore,
      this._callModalStore,
      this._contactCreateModalStore,
      this._entities
    )
    this._conversationSearchStore = new ConversationSearchStore(this._entities)

    this._conversationSearchStore.setConfig({
      onHasFilters: (value) => {
        this._conversationMenuStore.setDisabledActive(value)
      },
    })

    this._conversationMenuStore.setConfig({
      onOpenSearch: () => {
        this.onOpenSearch()
      },
      onToggleSearch: () => {
        this.onToggleSearch()
      },
      onShowChats: () => {
        if (['calls'].includes(uiStore.pathName)) {
          uiStore.changeRoute({
            path: `/${ConversationRoutes.root}`,
          })
        }
      },
    })

    this._callModalStore.setConfig({
      onSubmit: (teamId) => {
        const currentInboxId = this._entities.inboxesStore.currentInboxId
        const numericCurrentInboxId = currentInboxId !== null ? Number(currentInboxId) : null
        const numericTeamId = teamId !== null ? Number(teamId) : null

        if (numericCurrentInboxId !== numericTeamId && numericTeamId !== null) {
          this._conversationMenuStore.config?.resetConversations?.()
          this._conversationMenuStore.handleUpdateTeamInbox(numericTeamId)
        }
      },
    })
  }

  private _onOpenNewSearch = (value?: string) => {
    const pathname = `/${ConversationRoutes.root}/${ConversationRoutes.search}`

    if (!window.location.pathname.includes(pathname)) {
      logV2SearchEvent(
        'Search Icon Clicked',
        {
          event_id: 'search_icon_clicked',
        },
        this._entities
      )
    }

    openResultsSearch(value)
  }

  onOpenSearch = (value?: string) => {
    if (this._entities.featureFlagsStore.search_results_page) {
      this._onOpenNewSearch(value)
    } else {
      this._conversationSearchStore.onOpen(value)
    }
  }

  onToggleSearch = (value?: string) => {
    if (this._entities.featureFlagsStore.search_results_page) {
      this._onOpenNewSearch(value)
    } else {
      this._conversationSearchStore.onToggle()
    }
  }

  get entities() {
    return this._entities
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  get callStore() {
    return this._entities.callStore
  }

  get contactsDetailsManageStore() {
    return this._contactsDetailsManageStore
  }

  get conversationSearchStore() {
    return this._conversationSearchStore
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get conversationMenuStore() {
    return this._conversationMenuStore
  }

  get callModalStore() {
    return this._callModalStore
  }

  get contactCreateModalStore() {
    return this._contactCreateModalStore
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }
}
